import * as React from 'react';
import memoize from 'memoize-one';
import MediaQuery from 'react-responsive';
import { AnalyticsProvider } from '@telia-front/analytics';
import { EqualHeightProvider } from '@telia-front/equal-height';
import { ThemeContext } from '@telia-front/theme-provider';
import { ProcessListItem } from './process-list-item';
export const ProcessListContext = React.createContext({
    headingLevel: 3,
});
class ProcessList extends React.Component {
    constructor(props) {
        super(props);
        this.processList = null;
        this.firstChildCounter = null;
        this.lastChildCounter = null;
        this.progressLine = null;
        this.observer = null;
        this.equalheightGroups = memoize((extraGroups) => {
            const groupArray = ['process-list-icon', 'process-list-title'];
            if (extraGroups) {
                return groupArray.concat(extraGroups);
            }
            return groupArray;
        });
        this.setProgressLineStyles = () => {
            if (this.processList && this.firstChildCounter && this.lastChildCounter) {
                const firstCounterRect = this.firstChildCounter.getBoundingClientRect();
                const lastCounterRect = this.lastChildCounter.getBoundingClientRect();
                const processListRect = this.processList.getBoundingClientRect();
                const counterHalfWidth = firstCounterRect.width / 2;
                const counterHalfHeight = firstCounterRect.height / 2;
                const isHorizontal = this.props.break !== 'none' ? window.matchMedia(`(min-width: ${this.context.media.width[this.props.break || 'lg'].min})`).matches : false;
                if (isHorizontal) {
                    const width = lastCounterRect.right - firstCounterRect.left;
                    const nextLeft = `${firstCounterRect.left + counterHalfWidth - processListRect.left}px`;
                    const nextTop = `${firstCounterRect.top + counterHalfHeight - processListRect.top}px`;
                    const nextWidth = `${width - counterHalfWidth * 2}px`;
                    if (this.state.progressLineStyles.left !== nextLeft ||
                        this.state.progressLineStyles.top !== nextTop ||
                        this.state.progressLineStyles.width !== nextWidth) {
                        this.setState({
                            progressLineStyles: {
                                left: nextLeft,
                                top: nextTop,
                                width: nextWidth,
                            },
                        });
                    }
                }
                else {
                    const height = lastCounterRect.bottom - firstCounterRect.top;
                    const nextHeight = `${height - counterHalfHeight * 2}px`;
                    const nextLeft = `${firstCounterRect.left + counterHalfWidth - 1 - processListRect.left}px`;
                    const nextTop = `${firstCounterRect.top + counterHalfHeight - processListRect.top}px`;
                    if (this.state.progressLineStyles.height !== nextHeight ||
                        this.state.progressLineStyles.left !== nextLeft ||
                        this.state.progressLineStyles.top !== nextTop) {
                        this.setState({
                            progressLineStyles: {
                                height: nextHeight,
                                left: nextLeft,
                                top: nextTop,
                            },
                        });
                    }
                }
            }
        };
        this.handleScrolledToView = (entries) => {
            const entry = entries[0];
            if (!this.state.isOnScreen && entry.target === this.processList && entry.isIntersecting && this.observer) {
                this.setState({
                    isOnScreen: true,
                });
                this.observer.disconnect();
            }
        };
        this.handleLineTransitionEnd = (event) => {
            if (!(event.target === this.progressLine && event.propertyName === 'width')) {
                return;
            }
            this.setState({
                isProgressLineAnimated: true,
            });
        };
        this.setFirstChild = (el) => {
            if (el) {
                this.firstChildCounter = el.querySelector('.process-list__badge-wrapper');
            }
        };
        this.setLastChild = (el) => {
            if (el) {
                this.lastChildCounter = el.querySelector('.process-list__badge-wrapper');
            }
        };
        this.state = {
            isMounted: false,
            isOnScreen: false,
            isProgressLineAnimated: false,
            progressLineStyles: {},
        };
    }
    componentDidMount() {
        this.setState({
            isMounted: true,
        }, () => {
            this.setProgressLineStyles();
            this.observer = new IntersectionObserver(this.handleScrolledToView);
            if (this.processList) {
                this.observer.observe(this.processList);
            }
        });
    }
    componentWillUnmount() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
    BEM(stateClass) {
        const classArray = ['process-list', stateClass];
        if (this.props.type) {
            classArray.push('process-list--' + this.props.type);
        }
        if (this.props.break) {
            classArray.push('process-list--has-break-' + this.props.break);
        }
        if (this.state.isMounted && this.state.isOnScreen) {
            classArray.push('on-screen');
        }
        if (this.props.className) {
            classArray.push(this.props.className);
        }
        return classArray.join(' ');
    }
    progressLineBEM() {
        const classArray = ['process-list__progress-line'];
        if (this.state.isProgressLineAnimated) {
            classArray.push('process-list__progress-line--animated');
        }
        return classArray.join(' ');
    }
    getChildren() {
        const childrenCount = React.Children.count(this.props.children);
        return React.Children.map(this.props.children, (child, index) => {
            if (!React.isValidElement(child) || child.type !== ProcessListItem) {
                return child;
            }
            if (index === 0) {
                return React.cloneElement(child, {
                    elementRef: this.setFirstChild,
                    index,
                });
            }
            if (index + 1 === childrenCount) {
                return React.cloneElement(child, {
                    elementRef: this.setLastChild,
                    index,
                });
            }
            return React.cloneElement(child, {
                index,
            });
        });
    }
    renderProgressLine() {
        const transitionDuration = `${React.Children.count(this.props.children) * 500 + 100}ms`;
        const progressStyles = this.state.isOnScreen && this.state.isMounted ? this.state.progressLineStyles : {};
        return (React.createElement("div", { className: this.progressLineBEM(), ref: el => (this.progressLine = el), onTransitionEnd: this.handleLineTransitionEnd, style: Object.assign(Object.assign({}, progressStyles), { transitionDuration }) }));
    }
    renderContent(stateClass = 'is-vertical') {
        const { analyticsGroup, headingLevel = 3 } = this.props;
        return (React.createElement(AnalyticsProvider, { group: analyticsGroup },
            React.createElement(ProcessListContext.Provider, { value: {
                    headingLevel,
                } },
                React.createElement("div", { className: this.BEM(stateClass), ref: el => (this.processList = el) },
                    React.createElement("ol", { className: "process-list__list", role: "list" },
                        React.createElement(EqualHeightProvider, { groups: this.equalheightGroups(this.props.equalheightGroups), onGroupHeightsChange: this.setProgressLineStyles, offSet: 11 }, this.getChildren())),
                    this.renderProgressLine()))));
    }
    render() {
        if (this.state.isMounted && this.props.break !== 'none') {
            return (React.createElement(MediaQuery, { minWidth: this.context.media.width[this.props.break || 'lg'].min }, matches => this.renderContent(matches ? 'is-horizontal' : 'is-vertical')));
        }
        return this.renderContent();
    }
}
ProcessList.contextType = ThemeContext;
ProcessList.defaultProps = {
    break: 'lg',
    analyticsGroup: 'How To Apply',
    headingLevel: 3,
};
export default ProcessList;
